// src/pages/AddNewPayment.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subMonths, startOfMonth } from "date-fns";

import { getPartnerOptions, getPartnerLabel } from "../utils/partnerMap";
import { useAuth } from "../auth/AuthContext";

import {
	getMonthlyTotalRevenue,
	submitPaymentInfo
} from "../utils/paymentApiService";

/* ----------------------------- HELPERS ----------------------------- */
const getPrevMonthDate = () => startOfMonth(subMonths(new Date(), 1));

/* ----------------------------- UI HELPERS (OUTSIDE) ----------------------------- */
const FieldLabel = ({ children }) => (
	<label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
		{children}
	</label>
);

const Card = ({ title, children }) => (
	<div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
		<div className="mb-3 text-sm font-semibold text-slate-900">{title}</div>
		{children}
	</div>
);

const Skeleton = React.memo(() => (
	<div className="space-y-3">
		<div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
		<div className="h-4 w-full animate-pulse rounded bg-slate-200" />
		<div className="h-4 w-5/6 animate-pulse rounded bg-slate-200" />
		<div className="h-4 w-full animate-pulse rounded bg-slate-200" />
		<div className="h-4 w-4/6 animate-pulse rounded bg-slate-200" />
		<div className="h-4 w-full animate-pulse rounded bg-slate-200" />
		<div className="h-4 w-3/4 animate-pulse rounded bg-slate-200" />
	</div>
));

const KV = ({ k, v, strong = false }) => (
	<div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2">
		<div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
			{k}
		</div>
		<div
			className={`text-sm text-right ${
				strong ? "font-semibold text-slate-900" : "text-slate-700"
			}`}
		>
			{v}
		</div>
	</div>
);

const PreviewVertical = React.memo(({ row }) => (
	<div className="rounded-lg border border-slate-200 bg-white">
		<div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
			Preview Summary
		</div>

		<div className="px-3 py-1">
			<KV
				k="Partner Name"
				v={row.partnerName}
				strong
			/>
			<KV
				k="Payment Month"
				v={row.paymentMonth}
			/>
			<KV
				k="Deduction Amount"
				v={`$${row.deductionAmount}`}
			/>
			<KV
				k="Actual Revenue"
				v={`$${row.actualRevenue}`}
			/>
			<KV
				k="Revenue After Deduction"
				v={`$${row.revenueAfterDeduction}`}
				strong
			/>
			<KV
				k="Payment Status"
				v={row.paymentStatus}
			/>
			<KV
				k="Invoice Status"
				v={row.invoiceStatus}
			/>
		</div>
	</div>
));

/* ----------------------------- COMPONENT ----------------------------- */
const AddNewPayment = () => {
	const { token, role, loading } = useAuth();
	const isAdmin = role === "fl-admin";

	const partnerKeys = useMemo(() => getPartnerOptions() || [], []);

	const [partnerAlias, setPartnerAlias] = useState("");
	const [monthDate, setMonthDate] = useState(() => getPrevMonthDate());
	const maxSelectableMonth = useMemo(() => getPrevMonthDate(), []);

	// ✅ keep deduction in state for preview + submit
	const [deductionInput, setDeductionInput] = useState("0");

	// ✅ uncontrolled input ref to prevent focus loss
	const deductionRef = useRef(null);

	// Backend defaults are uppercase
	const [invoiceStatus, setInvoiceStatus] = useState("PENDING");
	const [paymentStatus, setPaymentStatus] = useState("PENDING");

	// revenue fetch state
	const [isPreviewLoading, setIsPreviewLoading] = useState(false);
	const [previewError, setPreviewError] = useState("");
	const [previewReady, setPreviewReady] = useState(false);
	const [actualRevenue, setActualRevenue] = useState(null);

	const [isSubmitting, setIsSubmitting] = useState(false);

	const paymentMonth = useMemo(() => format(monthDate, "yyyy-MM"), [monthDate]);

	const deductionNumber = useMemo(() => {
		const n = Number(deductionInput || "0");
		if (!Number.isFinite(n)) return 0;
		return Math.max(0, n);
	}, [deductionInput]);

	/**
	 * ✅ API CALL ONLY WHEN partner/month/token changes
	 * ✅ Admin-only + token required
	 * ✅ Deduction/status changes do NOT trigger this effect
	 */
	useEffect(() => {
		// don't do anything until auth is resolved
		if (loading) return;

		// 🚫 hard stop: non-admin should never call this page's APIs
		if (!isAdmin) return;

		if (!token) return;

		let cancelled = false;
		const controller = new AbortController();

		const run = async () => {
			if (!partnerAlias) {
				setPreviewReady(false);
				setActualRevenue(null);
				setPreviewError("");
				setIsPreviewLoading(false);
				return;
			}

			setPreviewReady(false);
			setActualRevenue(null);
			setPreviewError("");
			setIsPreviewLoading(true);

			try {
				const totalRevenue = await getMonthlyTotalRevenue({
					partnerAlias,
					month: paymentMonth,
					token,
					signal: controller.signal
				});

				if (cancelled) return;

				setActualRevenue(Number(totalRevenue || 0));
				setPreviewReady(true);
			} catch (e) {
				if (cancelled) return;
				if (e?.name === "AbortError") return;

				console.error(e);
				setPreviewError(e?.message || "Failed to load revenue preview.");
				setPreviewReady(false);
				setActualRevenue(null);
			} finally {
				if (!cancelled) setIsPreviewLoading(false);
			}
		};

		run();

		return () => {
			cancelled = true;
			controller.abort();
		};
	}, [loading, isAdmin, partnerAlias, paymentMonth, token]);

	/**
	 * ✅ Preview updates locally when deduction/status changes
	 * ✅ revenueAfterDeduction can be negative (your requirement)
	 */
	const previewRow = useMemo(() => {
		if (!partnerAlias) return null;
		if (!previewReady) return null;
		if (actualRevenue == null) return null;

		const revenueAfterDeduction =
			Number(actualRevenue || 0) - Number(deductionNumber || 0);

		return {
			partnerAlias,
			partnerName: getPartnerLabel(partnerAlias),
			paymentMonth,
			deductionAmount: deductionNumber,
			actualRevenue,
			revenueAfterDeduction,
			paymentStatus,
			invoiceStatus
		};
	}, [
		partnerAlias,
		paymentMonth,
		previewReady,
		actualRevenue,
		deductionNumber,
		paymentStatus,
		invoiceStatus
	]);

	const canSubmit = useMemo(() => {
		return (
			!loading &&
			isAdmin &&
			!!token &&
			!!partnerAlias &&
			!!previewRow &&
			!isPreviewLoading &&
			!isSubmitting
		);
	}, [
		loading,
		isAdmin,
		token,
		partnerAlias,
		previewRow,
		isPreviewLoading,
		isSubmitting
	]);

	const onSubmit = async () => {
		if (!canSubmit || !previewRow) return;

		setIsSubmitting(true);
		try {
			const payload = {
				paymentMonth,
				actualRevenue: Number(previewRow.actualRevenue || 0),
				deductionAmount: deductionNumber,
				invoiceStatus,
				paymentStatus
			};

			await submitPaymentInfo({
				partnerAlias,
				payload,
				token
			});

			alert("Payment info submitted successfully.");
		} catch (e) {
			console.error(e);
			alert(e?.message || "Submit failed. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	// ✅ hard UI guard
	if (loading) return null;

	if (!isAdmin) {
		return (
			<section className="rounded-xl bg-white p-6 shadow-sm">
				<div className="text-sm text-slate-700">
					You don’t have permission to access this page.
				</div>
			</section>
		);
	}

	return (
		<section className="overflow-hidden rounded-xl bg-white shadow-sm">
			<div className="p-6">
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
					{/* LEFT: Form */}
					<div className="lg:col-span-8">
						<Card title="Payment Setup">
							<div className="space-y-4">
								<div>
									<FieldLabel>Choose a Partner</FieldLabel>
									<select
										className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500"
										value={partnerAlias}
										onChange={(e) => setPartnerAlias(e.target.value)}
									>
										<option value="">...</option>
										{partnerKeys.map((key) => (
											<option
												key={key}
												value={key}
											>
												{getPartnerLabel(key)}
											</option>
										))}
									</select>
								</div>

								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<div>
										<FieldLabel>Payment Month</FieldLabel>
										<DatePicker
											selected={monthDate}
											onChange={(d) => setMonthDate(d || getPrevMonthDate())}
											dateFormat="yyyy/MM"
											showMonthYearPicker
											maxDate={maxSelectableMonth}
											className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500"
										/>
										<p className="mt-1 text-xs text-slate-500">
											Default is previous month. Max is previous month.
										</p>
									</div>

									<div>
										<FieldLabel>Deduction</FieldLabel>
										<div className="flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 focus-within:border-slate-500">
											<span className="mr-2 text-sm text-slate-500">$</span>

											{/* ✅ Uncontrolled input: will NOT lose focus on re-renders */}
											<input
												ref={deductionRef}
												type="text"
												inputMode="numeric"
												pattern="[0-9]*"
												defaultValue={deductionInput}
												onChange={(e) => {
													const v = e.target.value.replace(/[^\d]/g, "");
													if (v !== e.target.value) e.target.value = v;
													setDeductionInput(v === "" ? "0" : v);
												}}
												className="w-full text-sm text-slate-900 outline-none"
											/>
										</div>
									</div>
								</div>

								<div className="h-px w-full bg-slate-200" />

								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<div>
										<FieldLabel>Invoice Status</FieldLabel>
										<select
											className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500"
											value={invoiceStatus}
											onChange={(e) => setInvoiceStatus(e.target.value)}
										>
											<option value="PENDING">PENDING</option>
											<option value="RECEIVED">RECEIVED</option>
										</select>
									</div>

									<div>
										<FieldLabel>Payment Status</FieldLabel>
										<select
											className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500"
											value={paymentStatus}
											onChange={(e) => setPaymentStatus(e.target.value)}
										>
											<option value="PENDING">PENDING</option>
											<option value="PAID">PAID</option>
										</select>
									</div>
								</div>

								<div className="pt-2">
									<button
										onClick={onSubmit}
										disabled={!canSubmit}
										className={`rounded-md px-6 py-3 text-sm font-semibold text-white shadow transition
											${
												canSubmit
													? "bg-emerald-500 hover:bg-emerald-600"
													: "cursor-not-allowed bg-slate-300"
											}`}
									>
										{isSubmitting ? "SUBMITTING..." : "SUBMIT PAYMENT INFO"}
									</button>

									{!partnerAlias ? (
										<p className="mt-2 text-xs text-slate-500">
											Select a partner to generate the preview.
										</p>
									) : isPreviewLoading ? (
										<p className="mt-2 text-xs text-slate-500">
											Loading preview for {paymentMonth}...
										</p>
									) : previewError ? (
										<p className="mt-2 text-xs text-red-600">{previewError}</p>
									) : null}
								</div>
							</div>
						</Card>
					</div>

					{/* RIGHT: Preview */}
					<div className="lg:col-span-4">
						<Card title="Preview">
							{!partnerAlias ? (
								<div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
									Select a partner to load the revenue preview.
								</div>
							) : isPreviewLoading ? (
								<Skeleton />
							) : previewError ? (
								<div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
									{previewError}
								</div>
							) : previewRow ? (
								<PreviewVertical row={previewRow} />
							) : (
								<div className="text-sm text-slate-600">No preview.</div>
							)}
						</Card>
					</div>
				</div>
			</div>
		</section>
	);
};

export default AddNewPayment;
